/**
 * The part of a capture session that has broken three times: wiring the two
 * countdowns and starting the first one.
 *
 * It lives here, outside the hook, so it can be driven by real `Countdown`
 * instances under fake timers (see __tests__/captureLoop.test.ts). Every past
 * failure in this spot was an ordering mistake — a callback registered too late,
 * or a clock stopped too early — and none of them was visible to typecheck or
 * lint. Only running the clocks shows it.
 */

/** The slice of `useCountdown`'s return value the loop needs. */
export interface CountdownHandle {
  setOnTick: (callback: ((seconds: number) => void) | null) => void
  setOnComplete: (callback: (() => void) | null) => void
  start: () => void
}

export interface StartCaptureLoopOptions {
  prep: CountdownHandle
  shot: CountdownHandle
  /** 0 or less means the project wants no prep beat at all. */
  prepSeconds: number
  setTick: (seconds: number) => void
  /** Queue the next shot, or end the session. Must read the latest state. */
  advance: () => void
  /** Take one photograph. */
  takeOne: () => Promise<void>
}

/**
 * Wire both clocks, THEN start the session.
 *
 * The order is the whole point. The shot clock is started by `advance`, and
 * `advance` can run in this very call (when there is no prep beat), so its
 * callbacks must already be in place. Registering them after an early return
 * left the shot clock running with nothing listening: the display froze on its
 * first number and no photograph was ever taken.
 */
export function startCaptureLoop({
  prep,
  shot,
  prepSeconds,
  setTick,
  advance,
  takeOne
}: StartCaptureLoopOptions): void {
  prep.setOnTick(setTick)
  prep.setOnComplete(() => advance())
  shot.setOnTick(setTick)
  shot.setOnComplete(() => {
    void takeOne().then(() => advance())
  })

  if (prepSeconds < 1) {
    advance()
    return
  }

  setTick(prepSeconds)
  prep.start()
}
